import clsx from "clsx";
import ExtLink from "components/common/ExtLink";
import IntLink from "components/common/IntLink";
import TelegramIcon from "components/icons/TelegramIcon";
import MaxIcon from "components/icons/MaxIcon";
import HeaderLogo from "components/logos/HeaderLogo";
import MobileLogo from "components/logos/MobileLogo";
import {
  ADDRESS,
  CONTACT_PHONE,
  MAX_HEAD_LINK,
  TELEGRAM_HEAD_LINK,
} from "config/constants";
import useMenus from "hooks/useMenus";
import { isAbsent, isNonEmptyArray } from "lib/utils";
import dynamic from "next/dynamic";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { IMenuItem, selectCities, useGetUserCartQuery } from "store/api";
import { selectIsBrowser } from "store/ui";
import HeaderAddress from "./HeaderAddress";
import HeaderCart from "./HeaderCart";
import HeaderContact from "./HeaderContact";
import useSlug from "hooks/useSlug";
import Ticker from "./Ticker";

// dynamic() без ssr:false → SSR HTML идентичен sync импорту (нет CLS),
// но код уходит в отдельный chunk → меньше main bundle → TBT падает.
// NB: HeaderAuthMbl в ssr:false-варианте давал CLS-регрессию (a2bbc1f) —
// здесь ssr=true, поэтому safe.
let HeaderAuthMbl = dynamic(() => import("./HeaderAuthMbl"));
let HeaderFlyoutMenu = dynamic(() => import("./HeaderFlyoutMenu"));
let HeaderMobileMenu = dynamic(() => import("./HeaderMobileMenu"));
let HeaderSearch = dynamic(() => import("./HeaderSearch/HeaderSearch"));

let CallbackModal = dynamic(() => import("components/common/CallbackModal"));

let SignInModal = dynamic(() => import("components/auth/SignInModal"));

let AddAddressModal = dynamic(
  () => import("components/auth/AddAddressModal/AddAddressModal"),
  { ssr: false }
);

export type Props = {
  menusSSR?: Record<string, IMenuItem[]>;
};

let Header = ({ menusSSR }: Props) => {
  const { queryLng } = useSlug();
  const { data: userCart } = useGetUserCartQuery(queryLng);
  const [isAddressMapModalOpen, setIsAddressMapModalOpen] = useState(false);

  const cities = useSelector(selectCities("ru"));
  const defaultCity = useMemo(
    () => (isNonEmptyArray(cities) ? cities[0] : null),
    [cities],
  );

  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);

  let isBrowser = useSelector(selectIsBrowser);

  let { menu, drawerMenu, leftMenu } = useMenus(menusSSR);

  const [isStuck, setIsStuck] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isAbsent(ref?.current) || !isBrowser) return;
    const cachedRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStuck(entry!.intersectionRatio < 1);
      },
      { threshold: [1] },
    );

    observer.observe(cachedRef);
    return () => observer.unobserve(cachedRef);
  }, [ref, isBrowser]);

  const onOpenAddressModal = () => {
    setIsAddressMapModalOpen(true);
    setIsMobileMenuOpen(false);

    const html = document.querySelector("html");

    if (html) {
      html.style.overflow = "auto";
    }
  };

  const deliveryAddress =
    userCart &&
      userCart.data &&
      userCart.data.cart &&
      userCart.data.cart.address &&
      userCart.data.cart.address.coords &&
      userCart.data.cart.address.coords.address
      ? userCart.data.cart.address.coords.address
      : ADDRESS;

  return (
    <>
      {/* Бегущая строка (site_alerts type='ticker'); нет активных записей —
          фолбэк на прежний headerMsg той же высоты (CLS-паттерн внутри Ticker). */}
      <Ticker fallback={defaultCity?.header?.headerMsg} />
      <header className="z-40 fixed lg:sticky shadow-bottom-sticky top-0 lg:-top-1 left-0 right-0 bg-white lg:bg-transparent">
        <div className="lg:bg-lightGrey lg:bg-spoons-pattern h-[85px] lg:h-full flex items-center justify-between custom-px ">
          <div className="flex items-center justify-start lg:justify-between lg:w-[40%] xl:w-1/3">
            <div className="flex flex-col items-center gap-[4px]">
              {/* CLS-fix: убрал isBrowser-гард. Раньше телефон рендерился только
                  после hydration (isBrowser=true) → desktop CLS, на mobile
                  hidden lg:block спасал, но re-render всё равно происходил.
                  Теперь рендерится всегда, на SSR с CONTACT_PHONE-константой,
                  на client может смениться на defaultCity?.phones[0] (одинаковой
                  длины → no shift). */}
              <div className="flex gap-[20px] items-center">
                <a
                  className={clsx(
                    "hidden lg:block font-semibold text-[#202020] tracking-tightest xl:tracking-tighter whitespace-nowrap leading-snug -ml-2 xl:ml-0 text-[17px]",
                  )}
                  href={`tel:+${defaultCity?.phones[0]?.replace(/\D/g, "") ||
                    CONTACT_PHONE.replace(/\D/g, "")
                    }`}
                >
                  {defaultCity?.phones[0] || CONTACT_PHONE}
                </a>

                <div className="gap-[10px] hidden lg:flex items-center">
                  <ExtLink href={TELEGRAM_HEAD_LINK} aria-label="Telegram" className=" hidden lg:block">
                    <TelegramIcon className="w-6 h-6 rounded" />
                  </ExtLink>
                  <ExtLink href={MAX_HEAD_LINK} aria-label="MAX" className=" hidden lg:block">
                    <MaxIcon className="w-6 h-6 rounded" />
                  </ExtLink>
                </div>
              </div>
              <button
                type="button"
                data-testid="callback-open-desktop"
                className="hidden lg:inline-flex items-center justify-center gap-[6px] h-[24px] px-[10px] rounded-full border border-[#FF6262] bg-transparent text-[#FF6262] text-xs font-bold leading-4 whitespace-nowrap hover:bg-[#FF6262]/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#202020] focus-visible:outline-offset-2"
                onClick={() => setIsCallbackOpen(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M7 3 4 4c-2 1 0 7 4 11s10 6 11 4l1-3-5-3-2 2c-2-1-3-2-4-4l2-2Z" />
                </svg>
                Обратный звонок
              </button>
            </div>

            <div className="lg:hidden">
              <HeaderMobileMenu
                onOpenCallback={() => setIsCallbackOpen(true)}
                deliveryAddress={deliveryAddress}
                onOpenAddressModal={onOpenAddressModal}
                drawerMenu={drawerMenu}
                leftMenu={leftMenu}
                isOpen={isMobileMenuOpen}
                setIsOpen={setIsMobileMenuOpen}
                setIsOpenCb={setIsSignInModalOpen}
              />
            </div>

            <div className="hidden lg:block max-w-[260px]">
              <HeaderSearch />
            </div>

            <IntLink
              title="На главную"
              href="/"
              className="lg:hidden ml-4 w-[72px] h-[25px] flex-shrink-0 relative"
              onClick={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
            >
              <MobileLogo />
            </IntLink>
          </div>

          <IntLink
            href="/"
            className="hidden lg:block w-[124px] py-2 flex-shrink-0 relative"
          >
            {isStuck ? <MobileLogo className="py-2" /> : <HeaderLogo />}
          </IntLink>

          <div
            className="flex items-center gap-6 justify-end gap-[8px] lg:gap-[40px] lg:w-[40%] "
            onClick={() => isMobileMenuOpen && setIsMobileMenuOpen(false)}
          >
            {/*<HeaderAuth setIsOpenCb={setIsSignInModalOpen} />*/}
            <div className="hidden lg:flex items-center gap-[10px] justify-start items-center">
              <HeaderAddress
                deliveryAddress={deliveryAddress}
                onOpenModal={onOpenAddressModal}
                className="m-0 h-[40px] max-h-[40px] border border-[1px] border-[#E5E7EB] lg:border-[#D62128]  bg-[#F4F4F4] lg:bg-[#F4F4F4]"
                setIsOpenCb={setIsSignInModalOpen}
              />
              <HeaderAuthMbl setIsOpenCb={setIsSignInModalOpen} />
            </div>
            <div className="lg:hidden">
              <HeaderAuthMbl setIsOpenCb={setIsSignInModalOpen} />
            </div>
            <HeaderContact />
            <div className="block lg:hidden">
              <HeaderSearch />
            </div>
            <div className="relative">
              <div className=" flex relative">
                {/*
								 */}
                <HeaderCart />
              </div>
            </div>
          </div>
        </div>
      </header>
      <div ref={ref}></div>
      <HeaderFlyoutMenu menu={menu} leftMenu={leftMenu} />

      {isCallbackOpen && (
        <CallbackModal
          isOpen={isCallbackOpen}
          onClose={() => setIsCallbackOpen(false)}
        />
      )}

      {isSignInModalOpen && <SignInModal setIsOpenCb={setIsSignInModalOpen} />}

      {isAddressMapModalOpen && (
        <AddAddressModal
          isOpen={isAddressMapModalOpen}
          closeModal={() => setIsAddressMapModalOpen(false)}
        />
      )}
    </>
  );
};

export default Header;
